/**
 * Blog Post Adapter
 * 
 * Converts between external API data structures and internal domain models.
 * Implements the Adapter pattern for clean separation of concerns.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import type { BlogPost as ApiBlogPost } from '../../lib/api';
import type { BlogPost as DomainBlogPost } from '../../types';

/**
 * Blog Post Adapter
 * 
 * Handles conversion between API and domain models
 */
export class BlogPostAdapter {
  /**
   * Convert API BlogPost to Domain BlogPost
   */
  public static toDomain(apiBlogPost: ApiBlogPost): DomainBlogPost {
    return {
      id: apiBlogPost.id,
      title: apiBlogPost.title,
      content: apiBlogPost.content,
      author: 'Ertugrul Kücükkaya', // Default author since API doesn't have this field
      publish_date: apiBlogPost.publish_date,
      date: apiBlogPost.date,
      published: apiBlogPost.published,
      excerpt: apiBlogPost.excerpt || undefined,
      category: undefined, // API doesn't have category field
      tags: undefined, // API doesn't have tags field
      created_at: apiBlogPost.created_at,
      updated_at: apiBlogPost.updated_at || apiBlogPost.created_at, // Use created_at as fallback
    };
  }

  /**
   * Convert array of API BlogPosts to Domain BlogPosts
   */
  public static toDomainArray(apiBlogPosts: ApiBlogPost[]): DomainBlogPost[] {
    return apiBlogPosts.map(post => this.toDomain(post));
  }

  /**
   * Convert Domain BlogPost to API BlogPost
   */
  public static toApi(domainBlogPost: DomainBlogPost): ApiBlogPost {
    return {
      id: domainBlogPost.id,
      title: domainBlogPost.title,
      content: domainBlogPost.content,
      publish_date: domainBlogPost.publish_date || domainBlogPost.date,
      date: domainBlogPost.date,
      published: domainBlogPost.published,
      excerpt: domainBlogPost.excerpt || null,
      header_image: null, // API field not in domain
      audio_url: null, // API field not in domain
      created_at: domainBlogPost.created_at,
      updated_at: domainBlogPost.updated_at || null,
    };
  }

  /**
   * Validate blog post data
   */
  public static validate(blogPost: Partial<DomainBlogPost>): string[] {
    const errors: string[] = [];

    if (!blogPost.id || blogPost.id.trim().length === 0) {
      errors.push('Blog post ID is required');
    }

    if (!blogPost.title || blogPost.title.trim().length === 0) {
      errors.push('Blog post title is required');
    }

    if (!blogPost.content || blogPost.content.trim().length === 0) {
      errors.push('Blog post content is required');
    }

    if (!blogPost.author || blogPost.author.trim().length === 0) {
      errors.push('Blog post author is required');
    }

    if (!blogPost.date || blogPost.date.trim().length === 0) {
      errors.push('Blog post date is required');
    }

    if (typeof blogPost.published !== 'boolean') {
      errors.push('Blog post published status must be a boolean');
    }

    return errors;
  }

  /**
   * Create a blog post slug from title
   */
  public static createSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Extract excerpt from content if not provided
   */
  public static extractExcerpt(content: string, maxLength = 200): string {
    if (!content) {return '';}
    
    // Remove HTML tags
    const plainText = content.replace(/<[^>]*>/g, '');
    
    if (plainText.length <= maxLength) {
      return plainText;
    }
    
    // Find the last complete sentence within the limit
    const truncated = plainText.substring(0, maxLength);
    const lastSentence = truncated.lastIndexOf('.');
    
    if (lastSentence > maxLength * 0.7) {
      return truncated.substring(0, lastSentence + 1);
    }
    
    // If no good sentence break, truncate at word boundary
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 0) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  }

  /**
   * Format publish date for display
   */
  public static formatPublishDate(date: string): string {
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }

  /**
   * Calculate reading time estimate
   */
  public static calculateReadingTime(content: string): number {
    if (!content) {return 0;}
    
    // Remove HTML tags and count words
    const plainText = content.replace(/<[^>]*>/g, '');
    const wordCount = plainText.trim().split(/\s+/).length;
    
    // Average reading speed is 200-250 words per minute
    const wordsPerMinute = 225;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    
    return Math.max(1, minutes);
  }
} 